import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ChevronDownIcon, ChevronRightIcon, XMarkIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PlacementEditor, usePlaceableGroups } from './AddRosterSongModal';
import { useToast } from '../shared/Toast';

const ROSTER_URL = `${process.env.REACT_APP_API_URL}/api/roster`;
const STREAMING_URL = `${process.env.REACT_APP_API_URL}/streaming`;

const SECTION_OPTIONS = [
  { key: 'active', label: 'Active Artists' },
  { key: 'distro_artist', label: 'Distro Artists' },
  { key: 'distro_partner', label: 'Distro Partners' },
  { key: 'singles', label: 'Singles' },
  { key: 'off_roster', label: 'Off Roster' },
];

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const pct = (v) => {
  const n = Number(v) || 0;
  return <span className={n > 0 ? 'text-green-600' : n < 0 ? 'text-red-600' : 'text-gray-500'}>{n.toFixed(1)}%</span>;
};
const errMsg = (e) => e?.response?.data?.error || e.message;

// Flatten every placeable group (partners' child artists included) for pickers.
const allGroups = (sections) =>
  sections.filter((s) => s.key !== 'removed').flatMap((s) => s.groups.flatMap((g) => [g, ...g.children]));

function MetricCells({ m, bold }) {
  return (
    <>
      <td className={`px-3 py-1.5 text-right tabular-nums ${bold ? 'font-semibold' : ''}`}>{fmt(m?.last_complete_week_streams)}</td>
      <td className="px-3 py-1.5 text-right">{m ? pct(m.week_over_week_change_percent) : '—'}</td>
      <td className={`px-3 py-1.5 text-right tabular-nums ${bold ? 'font-semibold' : ''}`}>{fmt(m?.us_last_complete_week_streams)}</td>
      <td className="px-3 py-1.5 text-right">{m ? pct(m.us_week_over_week_change_percent) : '—'}</td>
    </>
  );
}

function SongRows({ songs, group, onAction, busy }) {
  return songs.map((s) => (
    <tr key={`${group.id}-${s.song_id}`} className="border-t border-gray-100 hover:bg-gray-50">
      <td className="px-3 py-1.5 pl-8 text-gray-500 truncate max-w-[14rem]">{s.display_artist_name}</td>
      <td className="px-3 py-1.5 truncate max-w-[16rem]">{s.title}</td>
      <MetricCells m={s.metrics} />
      <td className="px-3 py-1.5 text-right whitespace-nowrap">
        {group.kind !== 'removed' ? (
          <>
            <button disabled={busy} onClick={() => onAction('classify', { song: s })}
              className="text-xs text-blue-600 hover:text-blue-800 mr-3" title="Change where this song sits">Classify</button>
            <button disabled={busy} onClick={() => onAction('remove', { song: s })}
              className="text-xs text-red-600 hover:text-red-800" title="Take off the roster entirely">Remove</button>
          </>
        ) : (
          <button disabled={busy} onClick={() => onAction('restore', { song: s })}
            className="text-xs text-blue-600 hover:text-blue-800">Restore</button>
        )}
      </td>
    </tr>
  ));
}

function GroupBlock({ group, depth = 0, onAction, busy }) {
  const [open, setOpen] = useState(false);
  const headline = group.kind === 'partner' ? group.total : group.catalog;
  const count = group.songs.length + group.children.reduce((n, c) => n + c.songs.length, 0);
  return (
    <>
      <tr className={`border-t border-gray-200 ${group.kind === 'partner' ? 'bg-amber-50' : 'bg-yellow-50/60'}`}>
        <td className="px-3 py-1.5 font-semibold" style={{ paddingLeft: `${0.75 + depth * 1.25}rem` }}>
          <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1">
            {open ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
            {group.name}
          </button>
          <span className="ml-2 text-xs font-normal text-gray-500">{count} songs</span>
          {group.kind === 'artist' && group.recalculating && (
            <span className="ml-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Recalculating…</span>
          )}
          {group.kind === 'artist' && !group.recalculating && group.recalc_status === 'no_data' && !group.catalog_updated_at && (
            <span className="ml-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" title={group.recalc_message || ''}>
              No catalog data
            </span>
          )}
          {group.kind === 'artist' && !group.recalculating && group.recalc_status === 'error' && (
            <span className="ml-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700" title={group.recalc_message || ''}>
              Recalc failed
            </span>
          )}
          {group.kind === 'artist' && !group.recalculating && group.recalc_status !== 'error'
            && !(group.recalc_status === 'no_data' && !group.catalog_updated_at) && group.catalog_stale && (
            <span className="ml-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
              title={group.catalog_updated_at ? `Catalog last calculated ${new Date(group.catalog_updated_at).toLocaleString()}` : 'Catalog not calculated yet'}>
              Needs recalc
            </span>
          )}
        </td>
        <td className="px-3 py-1.5 font-semibold">{group.kind === 'artist' ? 'Catalog' : ''}</td>
        <MetricCells m={headline} bold />
        <td className="px-3 py-1.5 text-right whitespace-nowrap">
          {group.kind !== 'removed' && (
            <>
              {group.kind === 'artist' && (
                <button onClick={() => onAction('findSongs', { group })}
                  className="text-xs text-blue-600 hover:text-blue-800 mr-3" title="Place unassigned songs that match this artist">
                  Find songs
                </button>
              )}
              {group.kind === 'artist' && (
                <button disabled={busy || group.recalculating} onClick={() => onAction('recalculate', { group })}
                  className="text-xs text-gray-600 hover:text-gray-900 mr-3 disabled:opacity-40"
                  title="Rebuild this artist's Catalog row and Artists tab numbers (runs in the background)">
                  Recalculate
                </button>
              )}
              <button onClick={() => onAction('addSongs', { group })} className="text-xs text-blue-600 hover:text-blue-800 mr-3 inline-flex items-center gap-0.5">
                <PlusIcon className="h-3.5 w-3.5" />Song
              </button>
              <button onClick={() => onAction('editGroup', { group })} className="text-gray-500 hover:text-gray-800" title="Edit group">
                <PencilSquareIcon className="h-4 w-4 inline" />
              </button>
            </>
          )}
        </td>
      </tr>
      {open && (
        <>
          {group.children.map((c) => <GroupBlock key={c.id} group={c} depth={depth + 1} onAction={onAction} busy={busy} />)}
          <SongRows songs={group.songs} group={group} onAction={onAction} busy={busy} />
          {group.songs.length === 0 && group.children.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-2 pl-8 text-xs text-gray-400">No songs</td></tr>
          )}
        </>
      )}
    </>
  );
}

// Unassigned songs that could belong to an artist, with checkboxes.
function CandidatePicker({ artistName, selected, onChange }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const name = (artistName || '').trim();
    if (name.length < 2) { setResults([]); onChange([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true); setError('');
      try {
        const res = await axios.get(`${ROSTER_URL}/candidates`, { params: { artist_name: name } });
        if (cancelled) return;
        const songs = res.data.results || [];
        setResults(songs);
        // Default: tick songs credited to the artist; name-only matches stay opt-in.
        onChange(songs.filter((s) => s.match === 'credited').map((s) => s.song_id));
      } catch (e) {
        if (!cancelled) setError(errMsg(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistName]);

  const allOn = results.length > 0 && selected.length === results.length;
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="border rounded-md">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b text-xs">
        <span className="font-medium text-gray-700">
          Unassigned songs matching "{(artistName || '').trim()}" {loading ? '…' : `(${results.length})`}
        </span>
        {results.length > 0 && (
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={allOn} onChange={() => onChange(allOn ? [] : results.map((s) => s.song_id))} />
            Select all
          </label>
        )}
      </div>
      {error && <p className="px-3 py-2 text-xs text-red-600">{error}</p>}
      <div className="max-h-64 overflow-y-auto divide-y">
        {!loading && results.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">No unassigned songs match.</p>}
        {results.map((s) => (
          <label key={s.song_id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50">
            <input type="checkbox" checked={selected.includes(s.song_id)} onChange={() => toggle(s.song_id)} />
            <span className="min-w-0 flex-1">
              <span className="block truncate">{s.title}</span>
              <span className="block text-xs text-gray-500 truncate">{s.display_artist_name} · {fmt(s.metrics?.last_complete_week_streams)} last week</span>
            </span>
            <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded ${s.match === 'credited' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
              title={s.match === 'credited' ? 'Artist is credited (non-featured) on the song' : "Artist name appears in the song's artist name"}>
              {s.match === 'credited' ? 'Credited' : 'Name match'}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function FindSongsModal({ group, onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true); setError('');
    try {
      await axios.post(`${ROSTER_URL}/groups/${group.id}/songs`, { song_ids: selected });
      onSaved(selected.length);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={`Find songs for ${group.name}`} onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        <CandidatePicker artistName={group.catalog_artist_name || group.name} selected={selected} onChange={setSelected} />
        {error && <p className="text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded border">Cancel</button>
          <button onClick={save} disabled={saving || selected.length === 0} className="px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-50">
            {saving ? 'Adding…' : `Add ${selected.length} song${selected.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GroupModal({ group, groups, onClose, onSaved }) {
  const isNew = !group.id;
  const [form, setForm] = useState({
    name: group.name || '',
    kind: group.kind || 'artist',
    section: group.section || 'active',
    parent_id: group.parent_id || '',
    catalog_artist_name: group.catalog_artist_name || '',
    is_active: group.is_active ?? true,
  });
  const [error, setError] = useState('');
  const [songIds, setSongIds] = useState([]);
  const partners = groups.filter((g) => g.kind === 'partner' && g.id !== group.id);
  const showCandidates = isNew && form.kind === 'artist';
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const save = async () => {
    setError('');
    const body = { ...form, parent_id: form.parent_id ? Number(form.parent_id) : null,
      ...(form.kind === 'partner' ? { section: 'distro_partner' } : {}),
      ...(showCandidates ? { song_ids: songIds } : {}) };
    try {
      const res = isNew
        ? await axios.post(`${ROSTER_URL}/groups`, body)
        : await axios.patch(`${ROSTER_URL}/groups/${group.id}`, body);
      onSaved(res.data.songs_linked || 0);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <Modal title={isNew ? 'New roster group' : `Edit ${group.name}`} onClose={onClose} wide={isNew}>
      <div className="space-y-3 text-sm">
        <label className="block">Name (as shown in the report)
          <input value={form.name} onChange={set('name')} className="mt-1 w-full border rounded px-2 py-1.5" />
        </label>
        {isNew && (
          <label className="block">Type
            <select value={form.kind} onChange={set('kind')} className="mt-1 w-full border rounded px-2 py-1.5">
              <option value="artist">Artist (has a Catalog row)</option>
              <option value="partner">Distro partner</option>
              <option value="singles">Song list (no Catalog row)</option>
            </select>
          </label>
        )}
        {form.kind === 'artist' && (
          <>
            <label className="block">Under distro partner
              <select value={form.parent_id} onChange={set('parent_id')} className="mt-1 w-full border rounded px-2 py-1.5">
                <option value="">— none —</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="block">Luminate artist name (Catalog row + auto-placing new songs)
              <input value={form.catalog_artist_name} onChange={set('catalog_artist_name')} placeholder={form.name}
                className="mt-1 w-full border rounded px-2 py-1.5" />
            </label>
          </>
        )}
        {!form.parent_id && form.kind !== 'partner' && (
          <label className="block">Section
            <select value={form.section} onChange={set('section')} className="mt-1 w-full border rounded px-2 py-1.5">
              {SECTION_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        )}
        {!isNew && (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_active} onChange={set('is_active')} /> Active (unchecked hides the group)
          </label>
        )}
        {showCandidates && (
          <CandidatePicker artistName={form.catalog_artist_name || form.name} selected={songIds} onChange={setSongIds} />
        )}
        {error && <p className="text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded border">Cancel</button>
          <button onClick={save} className="px-3 py-1.5 rounded bg-blue-600 text-white">
            {showCandidates && songIds.length ? `Save + place ${songIds.length} song${songIds.length === 1 ? '' : 's'}` : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AddSongsModal({ group, onClose, onSaved }) {
  const [source, setSource] = useState('db');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const [error, setError] = useState('');

  const search = async (e) => {
    e.preventDefault();
    setError(''); setSearching(true);
    try {
      const res = source === 'db'
        ? await axios.get(`${ROSTER_URL}/songs/search`, { params: { q: query } })
        : await axios.post(`${STREAMING_URL}/search-songs`, { query });
      setResults(res.data.results || []);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setSearching(false);
    }
  };

  const add = async (r) => {
    const key = r.song_id || r.id;
    setError(''); setAdding(key);
    try {
      const body = source === 'db' ? { song_ids: [r.song_id] } : { luminate_id: r.id };
      await axios.post(`${ROSTER_URL}/groups/${group.id}/songs`, body);
      setResults(results.filter((x) => (x.song_id || x.id) !== key));
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setAdding(null);
    }
  };

  return (
    <Modal title={`Add songs to ${group.name}`} onClose={onClose} wide>
      <div className="text-sm space-y-3">
        <div className="flex gap-4">
          <label className="flex items-center gap-1"><input type="radio" checked={source === 'db'} onChange={() => { setSource('db'); setResults([]); }} /> Songs already tracked</label>
          <label className="flex items-center gap-1"><input type="radio" checked={source === 'luminate'} onChange={() => { setSource('luminate'); setResults([]); }} /> Search Luminate (adds to the Encore dashboard)</label>
        </div>
        <p className="text-xs text-gray-500">A song sits in one group, so adding a song that is already placed moves it here.</p>
        <form onSubmit={search} className="flex gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Title or artist"
            className="flex-1 border rounded px-2 py-1.5" />
          <button disabled={searching || query.trim().length < 2} className="px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-50">
            {searching ? 'Searching…' : 'Search'}
          </button>
        </form>
        {error && <p className="text-red-600">{error}</p>}
        <div className="max-h-80 overflow-y-auto divide-y">
          {results.map((r) => {
            const key = r.song_id || r.id;
            return (
              <div key={key} className="flex items-center justify-between py-1.5">
                <div className="min-w-0">
                  <div className="truncate">{r.title}</div>
                  <div className="text-xs text-gray-500 truncate">{r.display_artist_name}{r.group ? ` · ${r.group}` : ''}</div>
                </div>
                <button disabled={adding !== null} onClick={() => add(r)}
                  className="ml-3 shrink-0 text-xs px-2 py-1 rounded border border-blue-600 text-blue-600 hover:bg-blue-50 disabled:opacity-50">
                  {adding === key ? 'Adding…' : 'Add'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

function ClassifyModal({ song, currentId, onClose, onSaved }) {
  const groups = usePlaceableGroups();
  const [selected, setSelected] = useState(currentId);
  const [error, setError] = useState('');
  const save = async () => {
    setError('');
    try {
      await axios.put(`${ROSTER_URL}/songs/${song.song_id}/placement`, { group_id: selected });
      onSaved();
    } catch (e) {
      setError(errMsg(e));
    }
  };
  return (
    <Modal title="Classify song" onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        <div className="font-medium">{song.display_artist_name} - {song.title}</div>
        <PlacementEditor groups={groups} selected={selected} suggested={null} onChange={setSelected} />
        {error && <p className="text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded border">Cancel</button>
          <button onClick={save} className="px-3 py-1.5 rounded bg-blue-600 text-white">Save</button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-gray-600/50 z-50 overflow-y-auto">
      <div className={`relative top-16 mx-auto p-5 border shadow-lg rounded-md bg-white ${wide ? 'w-[36rem]' : 'w-96'} max-w-[calc(100%-2rem)]`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Every node (partners' children included) in the roster payload.
const allNodes = (data) => (data?.sections || []).flatMap((s) => s.groups.flatMap((g) => [g, ...g.children]));

export default function RosterManager() {
  const qc = useQueryClient();
  const toast = useToast();
  // Recalculations started from this page: group id -> { toastId, name }.
  const recalcToasts = useRef({});
  const { data, isLoading, error } = useQuery({
    queryKey: ['roster'],
    queryFn: () => axios.get(ROSTER_URL).then((r) => r.data),
    // Poll while any artist recalculation is running so the badge clears itself.
    refetchInterval: (query) => {
      const d = query?.state?.data ?? query;
      const running = allNodes(d).some((n) => n.recalculating);
      return running ? 5000 : false;
    },
  });

  // Swap a recalculation's loading toast for a result once polling shows it finished.
  useEffect(() => {
    if (!data) return;
    const byId = Object.fromEntries(allNodes(data).map((n) => [n.id, n]));
    Object.entries(recalcToasts.current).forEach(([id, { toastId, name }]) => {
      const node = byId[id];
      if (node && node.recalculating) return;
      toast.dismiss(toastId);
      delete recalcToasts.current[id];
      if (node?.recalc_status === 'error') {
        toast.error(`Recalculation for ${name} failed`, node.recalc_message || 'Check the API logs.');
      } else if (node?.recalc_status === 'no_data') {
        toast.info(`Nothing to calculate for ${name}`, node.recalc_message);
      } else {
        toast.success('Recalculation complete', `${name}'s Catalog and Artists tab numbers are up to date.`);
      }
    });
  }, [data, toast]);

  // Leaving the panel stops polling: don't leave a spinner up forever.
  useEffect(() => () => {
    Object.values(recalcToasts.current).forEach(({ toastId, name }) => {
      toast.dismiss(toastId);
      toast.info(`${name} is still recalculating`, 'It keeps running in the background.');
    });
    recalcToasts.current = {};
  }, [toast]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  const refresh = () => qc.invalidateQueries({ queryKey: ['roster'] });

  const run = async (fn) => {
    setBusy(true); setActionError('');
    try { await fn(); await refresh(); } catch (e) { setActionError(errMsg(e)); } finally { setBusy(false); }
  };

  const onAction = (type, { group, song }) => {
    if (type === 'classify') return setModal({ type, song });
    if (type === 'findSongs') return setModal({ type, group });
    if (type === 'recalculate') {
      return run(async () => {
        const res = await axios.post(`${ROSTER_URL}/groups/${group.id}/recalculate`);
        if (recalcToasts.current[group.id]) return;  // already tracking this one
        const toastId = toast.loading(
          `Recalculating ${group.name}...`,
          res.data.already_running ? 'Already running in the background' : 'Processing in background, this can take a few minutes'
        );
        recalcToasts.current[group.id] = { toastId, name: group.name };
      });
    }
    if (type === 'addSongs') return setModal({ type, group });
    if (type === 'editGroup') return setModal({ type, group });
    if (type === 'remove') {
      if (!window.confirm(`Remove "${song.title}" from the roster? It moves to Removed and can be restored.`)) return;
      return run(() => axios.post(`${ROSTER_URL}/songs/${song.song_id}/remove`));
    }
    if (type === 'restore') return run(() => axios.post(`${ROSTER_URL}/songs/${song.song_id}/restore`));
  };

  if (isLoading) return <div className="py-12 text-center text-gray-500">Loading roster…</div>;
  if (error) return <div className="py-12 text-center text-red-600">{errMsg(error)}</div>;

  const groups = allGroups(data.sections);
  const removed = data.sections.find((s) => s.key === 'removed')?.groups[0];

  // Current group per song, for the Classify editor.
  const placementOf = {};
  data.sections.filter((s) => s.key !== 'removed').forEach((s) => s.groups.forEach((g) => {
    [g, ...g.children].forEach((n) => n.songs.forEach((song) => { placementOf[song.song_id] = n.id; }));
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Roster management</h2>
          <p className="text-sm text-gray-500">Roster groups behind the roster report. Each song sits in one group.</p>
        </div>
        <button onClick={() => setModal({ type: 'editGroup', group: {} })}
          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-3 rounded-lg">
          <PlusIcon className="h-4 w-4" />New group
        </button>
      </div>
      {actionError && <p className="text-sm text-red-600">{actionError}</p>}
      {notice && <p className="text-sm text-green-700">{notice}</p>}

      <div className="bg-white rounded-xl ring-1 ring-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2">Artist</th><th className="px-3 py-2">Title</th>
              <th className="px-3 py-2 text-right">Global Last Week</th><th className="px-3 py-2 text-right">Global %</th>
              <th className="px-3 py-2 text-right">US Last Week</th><th className="px-3 py-2 text-right">US %</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {data.sections.filter((s) => s.key !== 'removed').map((section) => (
              <React.Fragment key={section.key}>
                <tr className="bg-green-100"><td colSpan={7} className="px-3 py-1.5 font-bold text-green-900">{section.label}</td></tr>
                {section.groups.map((g) => <GroupBlock key={g.id} group={g} onAction={onAction} busy={busy} />)}
                {section.groups.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-2 text-xs text-gray-400">No groups</td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl ring-1 ring-gray-200 p-4">
        <h3 className="font-semibold mb-1">Unassigned <span className="text-sm font-normal text-gray-500">({data.unassigned.length})</span></h3>
        <p className="text-xs text-gray-500 mb-3">Encore songs not in any roster group (the old "Remaining Songs").</p>
        <div className="max-h-96 overflow-y-auto divide-y text-sm">
          {data.unassigned.map((s) => (
            <div key={s.song_id} className="flex items-center gap-3 py-1.5">
              <div className="min-w-0 flex-1">
                <div className="truncate">{s.title}</div>
                <div className="text-xs text-gray-500 truncate">{s.display_artist_name} · {fmt(s.metrics?.last_complete_week_streams)} last week</div>
              </div>
              <button disabled={busy} onClick={() => onAction('classify', { song: s })}
                className="text-xs px-2 py-1 rounded border border-blue-600 text-blue-600 disabled:opacity-40">Classify</button>
              <button disabled={busy} onClick={() => onAction('remove', { song: s })} className="text-gray-400 hover:text-red-600" title="Remove">
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
          {data.unassigned.length === 0 && <p className="text-xs text-gray-400 py-2">Nothing unassigned.</p>}
        </div>
      </div>

      {removed && (
        <div className="bg-white rounded-xl ring-1 ring-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              <GroupBlock group={removed} onAction={onAction} busy={busy} />
            </tbody>
          </table>
        </div>
      )}

      {modal?.type === 'editGroup' && (
        <GroupModal group={modal.group} groups={groups} onClose={() => setModal(null)} onSaved={(linked) => {
          setModal(null);
          setNotice(linked ? `Saved. ${linked} song${linked === 1 ? '' : 's'} placed in the group.` : 'Saved.');
          refresh();
        }} />
      )}
      {modal?.type === 'findSongs' && (
        <FindSongsModal group={modal.group} onClose={() => setModal(null)} onSaved={(n) => {
          setModal(null);
          setNotice(`${n} song${n === 1 ? '' : 's'} placed in ${modal.group.name}.`);
          refresh();
        }} />
      )}
      {modal?.type === 'classify' && (
        <ClassifyModal song={modal.song} currentId={placementOf[modal.song.song_id] ?? null}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />
      )}
      {modal?.type === 'addSongs' && (
        <AddSongsModal group={modal.group} onClose={() => setModal(null)} onSaved={refresh} />
      )}
    </div>
  );
}
